import { reportError } from '@ms/yammer-telemetry';
import type { Context, ReactElement } from 'react';
import React, { Component, cloneElement, createContext, useCallback, useContext, useMemo, useState } from 'react';

import type {
  CatchErrorProps,
  CatchErrorState,
  ErrorBoundaryCode,
  ErrorBoundaryContextValue,
  ErrorBoundaryProps,
} from './ErrorBoundary.types';

export const ErrorBoundaryContext: Context<ErrorBoundaryContextValue | undefined> = createContext<
  ErrorBoundaryContextValue | undefined
>(undefined);

export const useErrorBoundaryContext = (): ErrorBoundaryContextValue => {
  const contextValue = useContext(ErrorBoundaryContext);

  if (contextValue == null) {
    throw new Error('useErrorBoundaryContext must be used within an ErrorBoundary.');
  }

  return contextValue;
};

class CatchError extends Component<CatchErrorProps, CatchErrorState> {
  public state = { error: null };

  public static getDerivedStateFromError(error: Error): CatchErrorState {
    return { error };
  }

  public async componentDidCatch(error: Error) {
    const { getGraphqlApiError } = await import(
      /* webpackChunkName: "4-feat-get-graphql-api-error" */ '../../environment/errors'
    );

    const errorCode = this.props.name ? this.props.name + 'Failed' : 'ErrorBoundary';

    const errorBoundaryNameProp = this.props.name
      ? {
          errorBoundaryName: this.props.name,
        }
      : {};

    reportError({
      error: getGraphqlApiError(error) ?? error,
      eventProperties: {
        errorCode,
        ...errorBoundaryNameProp,
      },
    });

    if (this.props.onError) {
      this.props.onError(error, this.retry);
    }
  }

  private retry = () => {
    this.setState({ error: null });
    this.props.onRetry();
  };

  public render() {
    const { children, onRenderError } = this.props;
    const { error } = this.state;

    if (error) {
      if (onRenderError == null) {
        return null;
      }

      return onRenderError({ error, retry: this.retry });
    }

    return cloneElement(children, { fetchKey: this.props.fetchKey });
  }
}

export const ErrorBoundary = <T extends ErrorBoundaryCode<string, string>>({
  children,
  onRenderError,
  name,
  onError,
}: ErrorBoundaryProps<T>): ReactElement => {
  const [fetchKey, setFetchKey] = useState(0);

  const bumpFetchKey = useCallback(() => {
    setFetchKey((oldKey) => oldKey + 1);
  }, []);

  const contextValue = useMemo<ErrorBoundaryContextValue>(
    () => ({
      fetchKey,
      bumpFetchKey,
    }),
    [fetchKey, bumpFetchKey]
  );

  return (
    <ErrorBoundaryContext.Provider value={contextValue}>
      <CatchError
        name={name}
        onRenderError={onRenderError}
        onError={onError}
        onRetry={bumpFetchKey}
        fetchKey={fetchKey}
      >
        {children}
      </CatchError>
    </ErrorBoundaryContext.Provider>
  );
};
